import { produce } from 'immer';
import { AsyncStatus, DashboardQueuesType } from 'lib-shared-legacy-types';
import { Action, combineActions, handleActions } from 'redux-actions';

import { dashboardActions } from 'app/views/layout/views/dashboard/dashboard.actions';
import { UseAppSearchParamsResult } from 'utils/url/useAppSearchParams';

import { websocketActions } from '../../../app/middleware/websocket/actions/websocket';
import { dashboardActions as legacyDashboardActions } from '../actions/dashboard';
import { apiPathToSearchParams } from '../api/dashboard';
import {
  DashboardQueue,
  DashboardQueueApiQueryParams,
  DashboardQueueResponsePayload,
  DashboardQueuesTypeAction,
} from '../api/types';

export const {
  dashboardGetAllQueues,
  dashboardGetAllQueuesSucceeded,
  dashboardGetAllQueuesFailed,

  dashboardGetAgentQueues,
  dashboardGetAgentQueuesSucceeded,
  dashboardGetAgentQueuesFailed,

  dashboardGetQueuesById,
  dashboardGetQueuesByIdSucceeded,
  dashboardGetQueuesByIdFailed,

  dashboardReset,
  dashboardChangeQueuesType,

  dashboardWsStatisticsQueueChanged,

  dashboardGetAllQueuesV1,
  dashboardGetAllQueuesSucceededV1,
  dashboardGetAllQueuesFailedV1,

  dashboardGetAgentQueuesV1,
  dashboardGetAgentQueuesSucceededV1,
  dashboardGetAgentQueuesFailedV1,
} = legacyDashboardActions;

export interface DashboardState {
  meta: {
    ajaxAsyncStatus: AsyncStatus;
    wsAsyncStatus: AsyncStatus;
    errors?: Array<string>;
    currentPageParams: DashboardLocationQueryParams;
    nextPageParams: DashboardLocationQueryParams | null;
    prevPageParams: DashboardLocationQueryParams | null;
  };
  queues: Array<DashboardQueue>;

  /** @deprecated Only used for v1 requests */
  personalQueues: Array<DashboardQueue>;

  /** @deprecated Only used for v1 requests */
  statisticsQueues: Array<DashboardQueue>;

  /** New real-time dashboard. */
  realTime: {
    lastSearchParams?: UseAppSearchParamsResult<'/dashboard'>['params'];
  };
}

/** The query params for dashboard location. */
export interface DashboardLocationQueryParams
  extends DashboardQueueApiQueryParams {
  type: DashboardQueuesType;
}

interface DashboardChangedQueues {
  params: {
    dashboard: {
      dashboard_entries: Array<DashboardQueue>;
    };
    timestamp: string;
  };
}

const DASHBOARD_INITIAL_STATE: DashboardState = {
  meta: {
    ajaxAsyncStatus: 'succeeded',
    wsAsyncStatus: 'succeeded',
    currentPageParams: {
      type: DashboardQueuesType.Agent,
      sort_by: 'name:asc',
    },
    nextPageParams: null,
    prevPageParams: null,
  },
  queues: [],
  personalQueues: [],
  statisticsQueues: [],
  realTime: {},
};

export const dashboard = handleActions<DashboardState, any>(
  {
    [combineActions(
      dashboardGetAllQueues,
      dashboardGetAgentQueues,
      dashboardGetQueuesById,
      dashboardGetAllQueuesV1, // v1 reducer
      dashboardGetAgentQueuesV1, // v1 reducer
    ).toString()]: (state) => {
      return produce(state, (draft) => {
        draft.meta.ajaxAsyncStatus = 'loading';
      });
    },

    [combineActions(
      dashboardGetAllQueuesFailed,
      dashboardGetAgentQueuesFailed,
      dashboardGetQueuesByIdFailed,
      dashboardGetAllQueuesFailedV1, // v1 reducer
      dashboardGetAgentQueuesFailedV1, // v1 reducer
    ).toString()]: (state) => {
      return produce(state, (draft) => {
        draft.meta.ajaxAsyncStatus = 'failed';
      });
    },

    [combineActions(
      dashboardGetAllQueuesSucceeded,
      dashboardGetAgentQueuesSucceeded,
    ).toString()]: (state, action: Action<DashboardQueueResponsePayload>) => {
      const {
        meta: {
          currentPageParams: { type },
        },
      } = state;
      const {
        data,
        meta: { next_page, prev_page, current_page },
      } = action.payload;

      return produce(state, (draft) => {
        const nextPageParams = next_page
          ? ({
              ...apiPathToSearchParams(next_page),
              type,
            } as DashboardLocationQueryParams)
          : null;
        const prevPageParams = prev_page
          ? ({
              ...apiPathToSearchParams(prev_page),
              type,
            } as DashboardLocationQueryParams)
          : null;
        const currentPageParams: DashboardLocationQueryParams = {
          ...(current_page && apiPathToSearchParams(current_page)),
          type,
        };

        draft.meta.ajaxAsyncStatus = 'succeeded';
        draft.queues = data.dashboard_entries;
        draft.meta.nextPageParams = nextPageParams;
        draft.meta.prevPageParams = prevPageParams;
        draft.meta.currentPageParams = currentPageParams;
      });
    },

    [dashboardGetQueuesByIdSucceeded.toString()]: (
      state,
      action: Action<DashboardQueueResponsePayload>,
    ) => {
      return produce(state, (draft) => {
        draft.meta.ajaxAsyncStatus = 'succeeded';
        draft.queues = action.payload.data.dashboard_entries;
        draft.meta.nextPageParams = null;
        draft.meta.prevPageParams = null;
        draft.meta.currentPageParams = {
          type: DashboardQueuesType.Favourite,
          sort_by: state.meta.currentPageParams.sort_by,
        };
      });
    },

    // v1 reducer
    [dashboardGetAllQueuesSucceededV1.toString()]: (
      state,
      action: Action<DashboardQueueResponsePayload>,
    ) => {
      return produce(state, (draft) => {
        draft.meta.ajaxAsyncStatus = 'succeeded';
        draft.statisticsQueues = action.payload.data.dashboard_entries;
      });
    },

    // v1 reducer
    [dashboardGetAgentQueuesSucceededV1.toString()]: (
      state,
      action: Action<DashboardQueueResponsePayload>,
    ) => {
      return produce(state, (draft) => {
        draft.meta.ajaxAsyncStatus = 'succeeded';
        draft.personalQueues = action.payload.data.dashboard_entries;
      });
    },

    [dashboardWsStatisticsQueueChanged.toString()]: (
      state,
      action: Action<DashboardChangedQueues>,
    ) => {
      const { dashboard_entries: updatedQueues } =
        action.payload.params.dashboard;

      return produce(state, (draft) => {
        updatedQueues.forEach((updatedQueue) => {
          const queueIndex = state.queues.findIndex(
            (queue) => updatedQueue.queue_id === queue.queue_id,
          );

          if (queueIndex > -1) {
            draft.queues[queueIndex] = updatedQueue;
          }
        });
      });
    },

    [dashboardChangeQueuesType.toString()]: (
      state: DashboardState,
      action: Action<DashboardQueuesTypeAction>,
    ) => {
      return produce(state, (draft) => {
        draft.meta.currentPageParams = {
          type: action.payload.type,
          sort_by: state.meta.currentPageParams.sort_by,
        };
        draft.meta.nextPageParams = null;
        draft.meta.prevPageParams = null;
        draft.queues = [];
      });
    },

    // WebSocket
    [websocketActions.request.type]: (state) => {
      return produce(state, (draft) => {
        draft.meta.wsAsyncStatus = 'loading';
      });
    },

    [websocketActions.requestFulfilled.type]: (state) => {
      return produce(state, (draft) => {
        draft.meta.wsAsyncStatus = 'succeeded';
      });
    },

    [websocketActions.replyReceived.type]: (state) => {
      return produce(state, (draft) => {
        draft.meta.wsAsyncStatus = 'succeeded';
      });
    },

    [websocketActions.requestRejected.type]: (state) => {
      return produce(state, (draft) => {
        draft.meta.wsAsyncStatus = 'failed';
      });
    },

    // New real-time dashboard.
    [dashboardActions.setLastSearchParams.type]: (state, action) => {
      return produce(state, (draft) => {
        draft.realTime.lastSearchParams = action.payload.params;
      });
    },
  },
  DASHBOARD_INITIAL_STATE,
);
